// lib/latest-video-models.ts
// The fal video models of the 2026-08/09 wave that share one shape: a prompt, a duration, a
// resolution tier, an aspect ratio for text-to-video, and either a start image or none. Each has
// a text-to-video and an image-to-video endpoint, so the request picks one from whether a frame
// was supplied. Prices are per second of output, from fal's catalogue on 2026-10-04 (MiniMax at
// its post-promotion rate, the promotion ending 2026-10-15).
import type { LatestVideoParams } from '#types/types';

export const LATEST_VIDEO_MODEL_IDS = [
  'minimax-h3-max',
  'minimax-h3-max-turbo',
  'wan-3.0-prime',
  'grok-imagine-video-1.5',
  'kling-3-turbo',
] as const;
export type LatestVideoModelId = typeof LATEST_VIDEO_MODEL_IDS[number];
export const isLatestVideoModel = (id: unknown): id is LatestVideoModelId =>
  typeof id === 'string' && (LATEST_VIDEO_MODEL_IDS as readonly string[]).includes(id);

type Spec = {
  textEndpoint: string;
  imageEndpoint: string;
  resolutions: readonly string[];
  defaultResolution: string;
  minDuration: number;
  maxDuration: number;
  defaultDuration: number;
  aspectRatios: readonly string[];
  supportsAudio: boolean;
  supportsSeed: boolean;
  /** Credits per second of output, by resolution (one entry when the model has a single tier). */
  creditsPerSecond: Readonly<Record<string, number>>;
};

const MINIMAX_ASPECTS = ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'] as const;

export const LATEST_VIDEO_MODELS: Readonly<Record<LatestVideoModelId, Spec>> = {
  // fal: $0.05 / $0.08 / $0.16 per second at 480p / 768p / 1080p after the promotion.
  'minimax-h3-max': {
    textEndpoint: 'minimax/h3-max/text-to-video',
    imageEndpoint: 'minimax/h3-max/image-to-video',
    resolutions: ['480p', '768p', '1080p'], defaultResolution: '768p',
    minDuration: 2, maxDuration: 15, defaultDuration: 5,
    aspectRatios: MINIMAX_ASPECTS, supportsAudio: false, supportsSeed: true,
    creditsPerSecond: { '480p': 1.5, '768p': 2.5, '1080p': 5 },
  },
  // fal: $0.025 / $0.04 / $0.08 per second after the promotion.
  'minimax-h3-max-turbo': {
    textEndpoint: 'minimax/h3-max-turbo/text-to-video',
    imageEndpoint: 'minimax/h3-max-turbo/image-to-video',
    resolutions: ['480p', '768p', '1080p'], defaultResolution: '768p',
    minDuration: 2, maxDuration: 15, defaultDuration: 5,
    aspectRatios: MINIMAX_ASPECTS, supportsAudio: false, supportsSeed: true,
    creditsPerSecond: { '480p': 1, '768p': 1.5, '1080p': 2.5 },
  },
  // fal: $0.068 / $0.14 / $0.28 per second at 480p / 720p / 1080p, audio included.
  'wan-3.0-prime': {
    textEndpoint: 'alibaba/wan-3.0-prime/text-to-video',
    imageEndpoint: 'alibaba/wan-3.0-prime/image-to-video',
    resolutions: ['480p', '720p', '1080p'], defaultResolution: '720p',
    minDuration: 2, maxDuration: 30, defaultDuration: 5,
    aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'], supportsAudio: true, supportsSeed: true,
    creditsPerSecond: { '480p': 2, '720p': 4, '1080p': 8 },
  },
  // fal: $0.08 / $0.14 / $0.25 per second at 480p / 720p / 1080p.
  'grok-imagine-video-1.5': {
    textEndpoint: 'xai/grok-imagine-video/v1.5/text-to-video',
    imageEndpoint: 'xai/grok-imagine-video/v1.5/image-to-video',
    resolutions: ['480p', '720p', '1080p'], defaultResolution: '720p',
    minDuration: 1, maxDuration: 15, defaultDuration: 6,
    aspectRatios: ['16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16'], supportsAudio: false, supportsSeed: false,
    creditsPerSecond: { '480p': 2.5, '720p': 4, '1080p': 7.5 },
  },
  // fal: $0.14 per second, one tier (Pro).
  'kling-3-turbo': {
    textEndpoint: 'fal-ai/kling-video/v3/turbo/pro/text-to-video',
    imageEndpoint: 'fal-ai/kling-video/v3/turbo/pro/image-to-video',
    resolutions: ['1080p'], defaultResolution: '1080p',
    minDuration: 3, maxDuration: 15, defaultDuration: 5,
    aspectRatios: ['16:9', '9:16', '1:1'], supportsAudio: false, supportsSeed: false,
    creditsPerSecond: { '1080p': 4 },
  },
};

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export const resolveLatestVideoDuration = (modelId: LatestVideoModelId, value: unknown): number => {
  const spec = LATEST_VIDEO_MODELS[modelId];
  return Math.max(spec.minDuration, Math.min(spec.maxDuration, Math.round(finite(value) ?? spec.defaultDuration)));
};

export const resolveLatestVideoResolution = (modelId: LatestVideoModelId, value: unknown): string => {
  const spec = LATEST_VIDEO_MODELS[modelId];
  return typeof value === 'string' && spec.resolutions.includes(value) ? value : spec.defaultResolution;
};

export const estimateLatestVideoCredits = (modelId: LatestVideoModelId, params: Partial<LatestVideoParams> | null | undefined): number => {
  const spec = LATEST_VIDEO_MODELS[modelId];
  const perSecond = spec.creditsPerSecond[resolveLatestVideoResolution(modelId, params?.resolution)] ?? 1;
  return Math.max(1, Math.ceil(perSecond * resolveLatestVideoDuration(modelId, params?.duration)));
};

export const getDefaultLatestVideoParams = (modelId: LatestVideoModelId): LatestVideoParams => {
  const spec = LATEST_VIDEO_MODELS[modelId];
  return {
    prompt: '', seed: -1,
    duration: spec.defaultDuration, resolution: spec.defaultResolution, aspect_ratio: 'auto',
    ...(spec.supportsAudio ? { generate_audio: false } : {}),
  };
};

/** The user's explicit ratio when the model takes it, else the nearest one to the output frame. */
const resolveAspectRatio = (spec: Spec, requested: unknown, width?: number | null, height?: number | null): string => {
  if (typeof requested === 'string' && spec.aspectRatios.includes(requested)) return requested;
  if (!width || !height || width <= 0 || height <= 0) return spec.aspectRatios[0]!;
  const target = width / height;
  const ratioOf = (value: string) => { const [w, h] = value.split(':').map(Number); return w! / h!; };
  return spec.aspectRatios.reduce((best, candidate) =>
    Math.abs(ratioOf(candidate) - target) < Math.abs(ratioOf(best) - target) ? candidate : best);
};

/** The fal endpoint and its exact input for one generation; a start image switches to image-to-video. */
export const buildLatestVideoRequest = (
  modelId: LatestVideoModelId,
  params: LatestVideoParams,
  prompt: string,
  frame: { imageUrl?: string | null; width?: number | null; height?: number | null },
): { endpoint: string; input: Record<string, unknown> } => {
  const spec = LATEST_VIDEO_MODELS[modelId];
  const imageUrl = frame.imageUrl?.trim() || null;
  const duration = resolveLatestVideoDuration(modelId, params.duration);
  const resolution = resolveLatestVideoResolution(modelId, params.resolution);
  const aspect = resolveAspectRatio(spec, params.aspect_ratio, frame.width, frame.height);
  const seed = spec.supportsSeed && finite(params.seed) !== null && params.seed >= 0 ? { seed: Math.round(params.seed) } : {};
  const endpoint = imageUrl ? spec.imageEndpoint : spec.textEndpoint;

  if (modelId === 'minimax-h3-max' || modelId === 'minimax-h3-max-turbo') {
    return { endpoint, input: {
      prompt, duration, resolution: resolution.toUpperCase(), prompt_expansion_mode: 'balanced', ...seed,
      ...(imageUrl ? { image_url: imageUrl } : { aspect_ratio: aspect }),
    } };
  }
  if (modelId === 'wan-3.0-prime') {
    return { endpoint, input: {
      prompt, duration, resolution, audio: params.generate_audio === true, ...seed,
      ...(imageUrl ? { start_image_url: imageUrl } : { aspect_ratio: aspect }),
    } };
  }
  if (modelId === 'grok-imagine-video-1.5') {
    return { endpoint, input: { prompt, duration, resolution, ...(imageUrl ? { image_url: imageUrl } : { aspect_ratio: aspect }) } };
  }
  // kling-3-turbo: the duration is a string enum on fal.
  return { endpoint, input: { prompt, duration: String(duration), ...(imageUrl ? { image_url: imageUrl } : { aspect_ratio: aspect }) } };
};
